import type { PlainDate } from "@sa-nu/shared-utils/date";
import { useMemo } from "react";

import { computeCenteredRangeStart } from "../../_shared/compute-centered-range-start";
import type {
  CICOSelection,
  ReservationCalendarNavigationSize,
} from "../reservation-calendar";

import { useCICOState } from "./use-ci-co-state";

export function useCICO({
  minNight,
  maxNight,
  navigationSize,
  unsettledFirstNightDelta,
  onInit,
}: {
  minNight: PlainDate;
  maxNight: PlainDate;
  navigationSize: ReservationCalendarNavigationSize;
  unsettledFirstNightDelta: number;
  /** URL由来の初期CICOが有効だった場合にマウント時1回だけ呼ばれるコールバック */
  onInit?: (cico: CICOSelection) => void;
}) {
  const { cico, setCICO, shiftCICO, clearCICO } = useCICOState({
    minNight,
    maxNight,
    navigationSize,
    onInit,
  });

  // シェブロン連打中、ヘッダー表示用に cico を先行シフトしたプレビュー値
  const nextCICO = useMemo(() => {
    if (!cico || unsettledFirstNightDelta === 0) return cico;
    return {
      checkIn: cico.checkIn.add({ days: unsettledFirstNightDelta }),
      checkOut: cico.checkOut.add({ days: unsettledFirstNightDelta }),
    };
  }, [cico, unsettledFirstNightDelta]);

  return { cico, setCICO, shiftCICO, clearCICO, nextCICO };
}

/**
 * firstNight の境界クランプは jumpTo 側で適用される。
 */
export function computeCenteredFirstNight(
  cico: CICOSelection,
  navigationSize: ReservationCalendarNavigationSize,
): PlainDate {
  const nights = cico.checkIn.until(cico.checkOut).total({ unit: "day" });
  const midOffset = Math.floor(nights / 2);
  const centerNight = cico.checkIn.add({ days: midOffset });
  return computeCenteredRangeStart({
    centerNight,
    rangeSize: navigationSize,
  });
}
